/**
 * ``ListSortMenu`` — the per-panel **Sort by** gear menu for the operator
 * Platforms / Detections / Alerts list panels (REQ-UI-110).
 *
 * Ships the panel-header preferences gear (item 5) that ``chrome-spec.md``
 * §Panel header describes, composed in the dockview group-header right-actions
 * slot alongside :func:`MaximizeToggle` (see :func:`PanelHeaderActions`). It
 * renders a Radix :file:`@/components/ui/dropdown-menu` with two radio
 * groups — the **sort field** and the **direction** (asc/desc) — for whichever
 * of the three list panels is active in the group.
 *
 * # Persistence (the working-state / Profile join)
 *
 * The selection lives in the Profile ``list_sort`` preference
 * (``ProfileDocumentV1.list_sort.{platforms,detections,alerts}``; ADR-022 /
 * ADR-033). This menu (rendered in the dockview header, inside the app's
 * :class:`PreferencesProvider`) reads the live selection via
 * :func:`usePreferences` and writes it with ``setWorkingState`` — optimistic,
 * auto-persisted, and restored when a Profile is applied, exactly like every
 * other preference. The *panel body* reads the same ``list_sort`` slice and
 * applies the sort (see each panel) — so the gear and the list stay in lock-step
 * through the shared working state, with no prop-drilling across the dockview
 * content/header boundary.
 *
 * An **absent** panel selection (``undefined`` / ``null``) means the panel's
 * built-in default order (Platforms: display name asc; Detections / Alerts: time
 * desc). The menu surfaces that default as the *checked* field + direction (via
 * each panel's :data:`PANEL_SORT_CONFIG` default), so the operator always sees
 * the effective order — picking the already-default option simply rewrites the
 * same selection (harmless), and there is no confusing "nothing selected" state.
 *
 * # Accessibility (REQ-UI-059 / 094)
 *
 * Radix gives the gear keyboard parity for free: the trigger opens on
 * Enter/Space, the menu is a focus-trapped ``role="menu"`` with arrow-key
 * roving focus, and each option is a ``role="menuitemradio"`` with
 * ``aria-checked`` so a screen reader announces the active sort. The trigger
 * carries an ``aria-label`` naming the panel it sorts; the radio groups carry
 * ``aria-label`` "Sort field" / "Sort direction". The check glyph on the
 * selected item is a non-colour signal (REQ-UI-046).
 *
 * Cross-references:
 *   - REQ-UI-110 (operator list sort) / REQ-UI-046 (severity order, non-colour
 *     selection) / REQ-UI-059 / REQ-UI-094 (keyboard + labelled).
 *   - ADR-022 (user preferences) / ADR-024 §8 (panel-header affordances) /
 *     ADR-033 (Profiles).
 *   - shared/chrome-spec.md §Panel header (the per-panel preferences gear).
 *   - @/preferences/preferences-context (setWorkingState); @/lib/list-sort
 *     (the order the panels apply from this selection).
 */
import { SortAscending, SortDescending } from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { usePreferences } from "@/preferences/preferences-context";
import type {
  AlertListSort,
  DetectionListSort,
  ListSortSettings,
  PlatformListSort,
  TrackListSort,
} from "@/auth/session-types";
import type { SortDirection } from "@/lib/list-sort";

/**
 * The list panels that carry a Sort-by gear (REQ-UI-110). A subset of
 * :type:`PanelId`; the gear renders only for these in :func:`PanelHeaderActions`.
 */
export type SortablePanelId = "platforms" | "detections" | "tracks" | "alerts";

/** One selectable sort field — its persisted wire value + operator label. */
interface SortFieldOption<TField extends string> {
  /** The BFF ``*SortFieldLiteral`` value persisted in ``list_sort``. */
  readonly value: TField;
  /** Operator-facing label shown in the menu. */
  readonly label: string;
}

/**
 * Per-panel sort configuration: the panel's selectable fields (in menu order)
 * and its built-in default selection (the order used when ``list_sort`` has no
 * entry for the panel). Field ``value``s match the committed BFF enums exactly
 * (``_profiles_schema.py`` ``*SortFieldLiteral``) — adding a field is an
 * additive schema change, not a UI-only invention.
 */
interface PanelSortConfig<TSel extends { field: string; direction: SortDirection }> {
  /** The panel's display title (for the gear ``aria-label``). */
  readonly title: string;
  /** Selectable fields, in the order they appear in the menu. */
  readonly fields: readonly SortFieldOption<TSel["field"]>[];
  /** The built-in default selection (REQ-UI-110 default order). */
  readonly defaultSelection: TSel;
}

/**
 * The Platforms-panel sort config (REQ-UI-110): health (severity order),
 * display name (default, asc), last status time, detection count.
 */
const PLATFORM_SORT_CONFIG: PanelSortConfig<PlatformListSort> = {
  title: "Platforms",
  fields: [
    { value: "display_name", label: "Name (A–Z)" },
    { value: "health", label: "Health (most urgent)" },
    { value: "last_status_time", label: "Last status time" },
    { value: "detection_count", label: "Detection count" },
  ],
  defaultSelection: { field: "display_name", direction: "asc" },
};

/**
 * The Detections-panel sort config (REQ-UI-110): time (default, desc),
 * classification, detecting-platform name, confidence.
 */
const DETECTION_SORT_CONFIG: PanelSortConfig<DetectionListSort> = {
  title: "Detections",
  fields: [
    { value: "time", label: "Time" },
    { value: "classification", label: "Classification" },
    { value: "platform_name", label: "Platform" },
    { value: "confidence", label: "Confidence" },
  ],
  defaultSelection: { field: "time", direction: "desc" },
};

/**
 * The Alerts-panel sort config (REQ-UI-110): time (default, desc), priority,
 * type, source-platform name. The ``time`` field's wire value is ``"time"``;
 * the panel maps it onto the ``received_at`` column when it applies the sort.
 */
const ALERT_SORT_CONFIG: PanelSortConfig<AlertListSort> = {
  title: "Alerts",
  fields: [
    { value: "time", label: "Time" },
    { value: "priority", label: "Priority" },
    { value: "alert_type", label: "Type" },
    { value: "platform_name", label: "Platform" },
  ],
  defaultSelection: { field: "time", direction: "desc" },
};

/**
 * The Tracks-panel sort config (ADR-041 D-5; REQ-UI-170): head time (default,
 * desc), duration, member count, class, reporting-platform name.
 */
const TRACK_SORT_CONFIG: PanelSortConfig<TrackListSort> = {
  title: "Tracks",
  fields: [
    { value: "head_time", label: "Head time" },
    { value: "duration", label: "Duration" },
    { value: "count", label: "Detections" },
    // ``classification`` is deliberately absent since 2026-10-01 (REQ-UI-170
    // amended): the row shows its kind, not the head's class, so a sort by an
    // invisible value is not offered. The BFF literal still accepts it for a
    // Profile saved before the change; such a profile sorts by it silently
    // and the menu shows no field checked until the operator picks one.
    { value: "platform_name", label: "Platform" },
  ],
  defaultSelection: { field: "head_time", direction: "desc" },
};

/** Direction options (shared by every panel). */
const DIRECTION_OPTIONS: readonly { value: SortDirection; label: string }[] = [
  { value: "asc", label: "Ascending" },
  { value: "desc", label: "Descending" },
];

/**
 * Resolve a panel's config from its id. A plain switch (not a record) so the
 * return type narrows to the panel-specific config union member.
 */
function configFor(
  panelId: SortablePanelId,
):
  | PanelSortConfig<PlatformListSort>
  | PanelSortConfig<DetectionListSort>
  | PanelSortConfig<TrackListSort>
  | PanelSortConfig<AlertListSort> {
  switch (panelId) {
    case "platforms":
      return PLATFORM_SORT_CONFIG;
    case "detections":
      return DETECTION_SORT_CONFIG;
    case "tracks":
      return TRACK_SORT_CONFIG;
    case "alerts":
      return ALERT_SORT_CONFIG;
  }
}

/**
 * Read the live selection for ``panelId`` from a ``list_sort`` slice, or
 * ``null`` when the panel has no entry (→ the caller uses the panel default).
 */
function selectionFor(
  listSort: ListSortSettings | undefined,
  panelId: SortablePanelId,
): { field: string; direction: SortDirection } | null {
  const entry = listSort?.[panelId];
  return entry ?? null;
}

interface ListSortMenuProps {
  /** Which of the three sortable panels this gear controls. */
  panelId: SortablePanelId;
}

/**
 * Render the Sort-by gear + dropdown for ``panelId`` (REQ-UI-110).
 *
 * Reads the live ``list_sort`` selection from :func:`usePreferences` and writes
 * field / direction changes back via ``setWorkingState`` (the
 * round-tripped-rest-of-document optimistic writer). The effective selection
 * shown as checked is the persisted one, or the panel's built-in default when
 * absent.
 *
 * @param props - The panel id this gear sorts.
 */
export function ListSortMenu({ panelId }: ListSortMenuProps): React.ReactElement {
  const { workingState, setWorkingState } = usePreferences();
  const config = configFor(panelId);
  const listSort = workingState.list_sort;
  const persisted = selectionFor(listSort, panelId);
  // The effective selection: persisted, else the panel default (so the menu
  // always shows the order actually in effect — never an empty radio group).
  const effective = persisted ?? config.defaultSelection;

  /**
   * Persist a new selection for this panel, merging it into the existing
   * ``list_sort`` slice (so the OTHER panels' selections are preserved) and
   * round-tripping it through ``setWorkingState`` (which itself round-trips the
   * rest of the working-state document — REQ-AUTH-060). The patch carries the
   * full ``list_sort`` object since ``setWorkingState`` shallow-merges at the
   * top level of the Profile document.
   */
  const persist = (next: { field: string; direction: SortDirection }): void => {
    const merged: ListSortSettings = {
      ...listSort,
      // The field/direction unions are panel-specific on the wire; this site is
      // the one place they converge, so the cast is localised + justified.
      [panelId]: next as ListSortSettings[SortablePanelId],
    };
    void setWorkingState({ list_sort: merged });
  };

  const handleFieldChange = (field: string): void => {
    persist({ field, direction: effective.direction });
  };

  const handleDirectionChange = (direction: string): void => {
    persist({ field: effective.field, direction: direction as SortDirection });
  };

  const DirectionIcon = effective.direction === "asc" ? SortAscending : SortDescending;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          // Naming the panel keeps the affordance unambiguous when multiple
          // group headers each show a sort gear (REQ-UI-094).
          aria-label={`Sort ${config.title}`}
          title={`Sort ${config.title}`}
          data-testid={`list-sort-gear-${panelId}`}
          className={[
            // Header chrome — full-height, no background of its own; matches the
            // MaximizeToggle button alongside it (see :func:`PanelHeaderActions`).
            "flex h-full items-center rounded px-2 text-text-secondary",
            "hover:text-text-primary focus-visible:text-text-primary",
            "outline-none",
          ].join(" ")}
        >
          <DirectionIcon size={16} aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        data-testid={`list-sort-menu-${panelId}`}
        aria-label={`Sort ${config.title}`}
      >
        <DropdownMenuLabel>Sort by</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={effective.field}
          onValueChange={handleFieldChange}
          aria-label="Sort field"
        >
          {config.fields.map((field) => (
            <DropdownMenuRadioItem
              key={field.value}
              value={field.value}
              data-testid={`list-sort-field-${panelId}-${field.value}`}
            >
              {field.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>

        <DropdownMenuSeparator />

        <DropdownMenuLabel>Direction</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={effective.direction}
          onValueChange={handleDirectionChange}
          aria-label="Sort direction"
        >
          {DIRECTION_OPTIONS.map((dir) => (
            <DropdownMenuRadioItem
              key={dir.value}
              value={dir.value}
              data-testid={`list-sort-direction-${panelId}-${dir.value}`}
            >
              {dir.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
